import { inject, Injectable, OnDestroy } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { LoadingController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import {
  BehaviorSubject,
  catchError,
  filter,
  firstValueFrom,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
  throwError,
  timeout,
} from "rxjs";
import { SiteModel } from "src/app/store/models/asset.model";
import { FileModel, PdfInputHelpFile } from "src/app/store/models/file.model";
import {
  AddOCRNotMappedPropertyReqBodyModel,
  BannedWasteItemModel,
  HazardDisplayItemModel,
  MM_ControlSheetToPdfDataModel,
  MM_Hazard_EnvironmentModel,
  MM_Hazard_HealthModel,
  MM_HazardItem,
  MM_Identification,
  MM_InventoryListResponseModel,
  MM_InventoryModel,
  MM_InventoryStatusEnum,
  MM_OCRPropertiesLevelMapping,
  MM_RiskLevelRequestUpdateInfo_Model,
  MM_StateInfo,
  MMInventoryMasterModel,
  OCRExtractedDataModel,
  OCRPatchMaterialDetails,
  OCRPatchProperties,
  OCRValidatedPatch,
  StepType,
  UpdateRiskLevelResponseModel,
} from "src/app/store/models/materialManagement.model";
import { RoleTypeEnum } from "src/app/store/models/role.model";
import { StatusEnumModel } from "src/app/store/models/status.model";
import {
  sanitizeParticleSizeDistribution,
  sanitizeFlashPoint,
} from "./ocr-response-sanitization";
import { UserModel } from "src/app/store/models/user.model";
import { v4 as uuidv4 } from "uuid";
import {
  FirebaseService,
  MMLockModel,
} from "../../firebase-service/firebase.service";
import { HttpService } from "../../http-service/http-client.service";
import { ToastService } from "../../toast-service/toast.service";
import { UserService } from "../../user-service/user.service";
interface Stepper {
  title: string;
  type: StepType;
  enabled: boolean;
  iconActive: string;
  completed: boolean;
  iconEnabled: string;
  description?: string;
  iconCompleted: string;
}
@Injectable({
  providedIn: "root",
})
export class RegisterMaterialService implements OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  httpService = inject(HttpService);
  toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private loadingCtrl = inject(LoadingController);
  public firebaseService = inject(FirebaseService);
  userService = inject(UserService);
  public isOcrScanning: boolean = false;
  public selectedIdentificationFile: File | null = null;
  public isLocked = new BehaviorSubject<boolean>(false);
  public dropDownControl = new BehaviorSubject<boolean>(false);
  public isStepperExpanded = new BehaviorSubject<boolean>(true);
  public activeStep = new BehaviorSubject<StepType>("globalSearch");
  public isNotificationFlow$ = new BehaviorSubject<boolean>(false);
  public isRejectionModalOpen = new BehaviorSubject<boolean>(false);
  private addMaterialSubject = new BehaviorSubject<boolean | null>(null);
  private mmHazardsSubject = new BehaviorSubject<MM_HazardItem[] | null>(null);
  private riskLevelRequestUpdateDataSubject =
    new BehaviorSubject<MM_RiskLevelRequestUpdateInfo_Model | null>(null);
  riskLvlReqUpdateData$ = this.riskLevelRequestUpdateDataSubject.asObservable();
  public currentMMInventoryData: MM_InventoryModel;
  private mmInventoryMasterData = new BehaviorSubject<
    MMInventoryMasterModel[] | null
  >(null);
  private mmInventoryStateUnitData = new BehaviorSubject<MM_StateInfo[] | null>(
    null,
  );
  private mmHazardHealthDataSubject = new BehaviorSubject<
    MM_Hazard_HealthModel[] | null
  >(null);
  private environmentHazardSubject = new BehaviorSubject<
    MM_Hazard_EnvironmentModel[] | null
  >(null);
  environmentHazardData$ = this.environmentHazardSubject.asObservable();
  private hazardsSubject = new BehaviorSubject<{
    fireHazards: HazardDisplayItemModel[];
    healthHazards: HazardDisplayItemModel[];
    environmentHazards: HazardDisplayItemModel[];
  }>({
    fireHazards: [],
    healthHazards: [],
    environmentHazards: [],
  });
  private hazardsLoaded = false;
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();

  private materialTypes: MMInventoryMasterModel[];
  mmHazardList$ = this.mmHazardsSubject.asObservable();
  private autosaveCompleteSubject = new Subject<void>();
  mmControlSheetIntroduction$ = new BehaviorSubject<string>("");
  mmInventoryMasterData$ = this.mmInventoryMasterData.asObservable();
  mmControlSheetIntroductionPrompt$ = new BehaviorSubject<string>("");
  mmHazardHealthData$ = this.mmHazardHealthDataSubject.asObservable();
  public maxStepReached = new BehaviorSubject<number>(0);
  private propertyMappingLoaded$ = new BehaviorSubject<boolean>(false);
  mmInventoryStateUnitData$ = this.mmInventoryStateUnitData.asObservable();

  private propertyMappingData: MM_OCRPropertiesLevelMapping[] | null = null;
  private lockRefreshInterval: ReturnType<typeof setInterval> | null = null;

  public siteId: string = "";
  public siteName: string = "";
  public currentUser: UserModel | null = null;

  private leachateTestPdfSubject = new BehaviorSubject<FileModel | null>(null);
  leachateTestPdf$ = this.leachateTestPdfSubject.asObservable();

  private burnTestPdfSubject = new BehaviorSubject<FileModel | null>(null);
  burnTestPdfSubject$ = this.burnTestPdfSubject.asObservable();

  private bannedWasteDataSubject = new BehaviorSubject<BannedWasteItemModel[]>(
    [],
  );
  bannedWasteData$ = this.bannedWasteDataSubject.asObservable();

  constructor() {
    this.store.select("user").subscribe({
      next: (user: UserModel) => {
        this.currentUser = user;
      },
    });
    this.store
      .select("site")
      .pipe(
        filter((site) => !!site),
        takeUntil(this.destroy$),
      )
      .subscribe(async (site) => {
        this.siteId = site?.id!;
      });
    this.loadBannedWasteConfig();
  }
  // Add this method

  private loadBannedWasteConfig() {
    this.firebaseService
      .getBannedWasteConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (configs) => {
          if (configs && configs.length > 0) {
            const mappedData: BannedWasteItemModel[] = configs.map(
              (config) => ({
                title: config.title,
                isPresent: null,
                isShowPopup: false,
                popupText: config.popupText || "",
                isDisableSubmit: false,
              }),
            );
            this.bannedWasteDataSubject.next(mappedData);
            this.confirmationOfBannedWasteData = mappedData;
          }
        },
        error: () => {
          // Keep default if error
          console.error("Error loading banned waste config");
        },
      });
  }

  async checkHasMMExpertRole(): Promise<boolean> {
    const isMMExpert = await this.userService.CheckUserRoleExistByRoleName(
      RoleTypeEnum.MMExpert,
    );
    return isMMExpert;
  }

  public stepsName: StepType[] = [
    "globalSearch",
    "identification",
    "linkToAsset",
    "confirmation",
  ];
  private readonly STATE_MAP: Record<string, string[]> = {
    Solid: [
      "solid",
      "solide", // English, French (from your SDS)
      "sólido", // Spanish
      "твърд",
      "твърдо", // Bulgarian
      "szilárd", // Hungarian
    ],
    Liquid: ["liquid", "liquide", "líquido", "течен", "течност", "folyékony"],
    Gas: ["gas", "gazeux", "gaseoso", "газ", "gáz"],
    Sludge: ["sludge", "pâteux", "lodo", "утайка", "iszap"],
  };

  // ✅ NEW: Update maximum step reached (only increases, never decreases)
  public updateMaxStepReached(stepIndex: number): void {
    const currentMax = this.maxStepReached.getValue();
    if (stepIndex > currentMax) {
      this.maxStepReached.next(stepIndex);
    }
  }

  // ✅ NEW: Get step index safely
  public getStepIndex(step: StepType): number {
    return this.stepsName.indexOf(step);
  }

  // ✅ NEW: Check if step is accessible based on max progress
  public isStepAccessible(step: StepType): boolean {
    const stepIndex = this.getStepIndex(step);
    const maxReached = this.maxStepReached.getValue();
    // Allow access to completed steps + one step ahead
    return stepIndex <= maxReached + 1;
  }

  mmInventoryForm: FormGroup = this.fb.group({
    id: [""],
    siteId: [""],
    isEditMode: [false],
    masterMaterialId: [""],
    isConfirmEditing: [true],
    isPublished: [false],
    identificationFormGroup: this.fb.group({
      statusSection: this.fb.group({
        status: ["ACTIVE", Validators.required],
      }),
      sdsFileInfo: this.fb.group({
        name: [""],
        src: [""],
        id: [""],
        size: [""],
        isNewFile: [false],
      }),
      labSheets: this.fb.array([]),
      documentValidation: [null, Validators.required],
      materialDetails: this.fb.group({
        materialName: [
          "",
          [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(30),
          ],
        ],
        supplierName: ["", [Validators.required, Validators.maxLength(15)]],
        materialNameSlug: ["", Validators.required],
        state: ["", Validators.required],
        subType: ["", Validators.required],
        type: ["", Validators.required],
        isImpregnated: ["", Validators.required],
      }),

      consumtionVolume: this.fb.group({
        value: [null],
        unit: [""],
      }),
      environmentalPermitsStatus: this.fb.group({
        storageAndTransport: [""],
        preAndCoProcessing: [""],
        trailBurnPersistentOrganicPollutants: [""],
      }),
      properties: this.fb.group({
        pH: [null],
        odor: [null],
        lead: [null],
        dusty: [null],
        nickel: [null],
        mercury: [null],
        arsenic: [null],
        cadmium: [null],
        agent: [""],
        moisture: [null],
        pumpable: [null],
        burnTest: [null],
        antimony: [null],
        chromium: [null],
        fluoride: [null],
        viscosity: [null],
        particles: [null],
        flashPoint: [null],
        stickiness: [null],
        wasteOil: [""],
        substance: [""],
        flowability: [null],
        waterContent: [null],
        foreignBodies: [null],
        sedimentation: [null],
        totalNitrogen: [null],
        fireHPhrase: [[]],
        calorificValue: [null],
        typeOfLiquid: [""],
        totalPhosphorus: [null],
        healthHPhrase: [[]],
        wholeTires: [false],
        storedInEnclosedAsset: [false],
        doesntIgnite: [false],
        flammabilityRange: [null],
        leachateTest: [false],
        aqueousSolution: [""],
        biologicalWaste: [""],
        timeToIgnitionTest: [null],
        lowerFlammableLevel: [null],
        upperFlammableLevel: [null],
        untestedMaterials: [""],
        totalSuspendedSolids: [null],
        environmentHPhrase: [[]],
        nonAqueousSolution: [""],
        wasteTreatmentSludges: [""],
        particleSizeDistribution: [null],
        bodBiochemicalOxygenDemand: [null],
        totalPetroleumHydrocarbons: [null],
        environmentCategoryLiquid: [""],
        sustainedFlamingDurationTest: [null],
        naturalUntreatedBiomass: [false],
      }),

      criticalWastes: [[]],
      dartxClassification: [""],
      hazards: [[]],
      confirmationOfBannedWaste: [[]],
      riskLevel: this.fb.group({
        fireRiskLevel: [null],
        healthRiskLevel: [null],
        environmentRiskLevel: [null],
      }),
    }),

    linkToAssetFormGroup: this.fb.group({
      assets: this.fb.array([]),
      completedZone: [[]],
      simulatedAssetes: this.fb.array([]),
      addCompletedAssetZone: [[]],
    }),
    confirmationFormGroup: this.fb.group({
      assignTo: this.fb.group({
        userName: [""],
        email: [""],
      }),
      ccEmails: [[]],
      ccMail: this.fb.group({
        userName: [""],
        email: [""],
      }),
      subject: [""],
      mailText: [""],
    }),
    stepperInfo: this.fb.group({
      isCompleted: false,
      currentStepNo: 0,
    }),
    status: [MM_InventoryStatusEnum.DRAFT],
    isCertified: [false],
    isExpertFlow: [false],
    isClone: [false],
    cloneDataInfo: this.fb.group({
      parentId: [""],
      isCertified: [false],
    }),

    ocrFilledFields: [[]],
    shouldGenerateControlSheet: [false],
    controlSheetPdfInfo: [null],
  });

  riskLevelRequestUpdateForm: FormGroup = this.fb.group({
    id: [""],
    materialId: [""],
    fireRiskLevel: [0],
    healthRiskLevel: [0],
    environmentRiskLevel: [0],
    fireHazards: [[]],
    healthHazards: [[]],
    environmentHazards: [[]],
    remarks: ["", [Validators.maxLength(250), Validators.required]],
    rejectionReason: [""],
    requestStatus: [""],
    createdAt: [null],
    requestAction: this.fb.group({
      email: [""],
      remarks: [""],
      fullName: [""],
      createdAt: [null],
    }),
  });

  HAZARD_SECTIONS_CONFIG = [
    {
      label: "Fire",
      icon: "../../../../assets/settings/materialManagement/fire.svg",
      titles: ["not combustible", "combustible", "flammable", "explosive"],
    },
    {
      label: "Environment",
      icon: "../../../../assets/settings/materialManagement/environemnt.svg",
      titles: ["hazardous to environment"],
    },
    {
      label: "Health",
      icon: "../../../../assets/settings/materialManagement/health.svg",
      titles: ["acute toxicity", "health hazard", "serious health hazard"],
    },
  ];
  // You should probably move this here too to keep them in sync!
  ALL_HAZARDS_MASTER_LIST = [
    {
      title: "Flammable",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/flammable.svg",
    },
    {
      title: "Non-Flammable",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/nonFlammable.avif",
    },
    {
      title: "Combustible",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/combustible.svg",
    },
    {
      title: "Oxidizing",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/oxidizing.svg",
    },
    {
      title: "Health hazard",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/healthHazard.svg",
    },
    {
      title: "Acute toxicity",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/acuteToxicity.svg",
    },
    {
      title: "Serious health hazard",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/seriousHealthHazard.svg",
    },
    {
      title: "Corrosive",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/corrosive.svg",
    },
    {
      title: "Environmentally damaging",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/environmentallyDam.svg",
    },
    {
      title: "Liable to spontaneous combustion",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/liableToSpont.svg",
    },
    {
      title: "Contact with water emit flammable gases",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/contactWithWater.svg",
    },
    {
      title: "Can form explosive atmosphere",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/explosiveAtm.svg",
    },
    {
      title: "Compressed gas",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/compressedGas.svg",
    },
    {
      title: "Explosive",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/explosive.svg",
    },
    {
      title: "Infectious substances",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/infectousSubs.svg",
    },
    {
      title: "Radioactive",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/radioactive.svg",
    },
    {
      title: "Not combustible",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/notCombustible.svg",
    },
    {
      title: "Hazardous to environment",
      iconPath:
        "../../../../assets/settings/materialManagement/Hazards/hazardousToEnvironment.svg",
    },
  ];

  stepper: Stepper[] = [
    {
      enabled: true,
      completed: false,
      type: "globalSearch",
      title: this.translate.instant("STEPPER_GLOBAL_SEARCH"),
      iconActive: "assets/settings/materialManagement/stepper/step1-active.svg",
      iconEnabled:
        "assets/settings/materialManagement/stepper/step1-enabled.svg",
      iconCompleted:
        "assets/settings/materialManagement/stepper/step1-completed.svg",
      description: `
      <div style="margin-bottom: 8px;">${this.translate.instant("STEPPER_SELECT_MATERIAL")}</div>
      <div style="padding-left: 50px; text-indent: -15px; margin-bottom: 4px;">● ${this.translate.instant("STEPPER_MULTI_SITE_USAGE")}</div>
      <div style="padding-left: 50px; text-indent: -15px;">● ${this.translate.instant("STEPPER_PROPERTY_CHANGE")}</div>
    `,
    },
    {
      enabled: false,
      completed: false,
      type: "identification",
      title: this.translate.instant("STEPPER_CLASSIFICATION"),
      iconActive:
        "../../../../assets//settings//materialManagement/stepper/classificationDisable.svg",
      iconEnabled:
        "../../../../assets//settings//materialManagement/stepper/classificationActive.svg",
      iconCompleted:
        "../../../../assets//settings//materialManagement/stepper/classificationComplete.svg",
      description: `
      <div style="margin-bottom: 8px;"> ${this.translate.instant("STEPPER_UPLOAD_SDS")}</div>
      <div style="padding-left: 50px; text-indent: -15px; margin-bottom: 4px;">● ${this.translate.instant("STEPPER_MANUAL_OVERRIDE")}</div>
      <div style="padding-left: 50px; text-indent: -15px; margin-bottom: 4px;">● ${this.translate.instant("STEPPER_ADDITIONAL_TEST")}</div>
      <div style="padding-left: 50px; text-indent: -15px;">● ${this.translate.instant("STEPPER_RISK_LEVEL")}</div>
    `,
    },
    // {
    //   enabled: false,
    //   completed: false,
    //   type: "hazard",
    //   title: "3. Hazard",
    //   iconActive: "assets/settings/materialManagement/stepper/step3-active.svg",
    //   iconEnabled:
    //     "assets/settings/materialManagement/stepper/step3-enabled.svg",
    //   iconCompleted:
    //     "assets/settings/materialManagement/stepper/step3-completed.svg",
    //   description: `● Enter the Material specifications to estabish its hazard level`,
    // },
    {
      enabled: false,
      completed: false,
      type: "linkToAsset",
      title: this.translate.instant("STEPPER_LINK_TO_ASSETS"),
      iconActive: "assets/settings/materialManagement/stepper/step4-active.svg",
      iconEnabled:
        "assets/settings/materialManagement/stepper/step4-enabled.svg",
      iconCompleted:
        "assets/settings/materialManagement/stepper/step4-completed.svg",
      description: `● ${this.translate.instant("STEPPER_MAP_SELECTION")}
      </br>
● ${this.translate.instant("STEPPER_REVIEW")}</br>
● ${this.translate.instant("STEPPER_MULTI_MATERIAL")}
`,
    },
    {
      enabled: false,
      completed: false,
      type: "confirmation",
      title: this.translate.instant("STEPPER_CONFIRMATION"),
      iconActive: "assets/settings/materialManagement/stepper/step5-active.svg",
      iconEnabled:
        "assets/settings/materialManagement/stepper/step5-enabled.svg",
      iconCompleted:
        "assets/settings/materialManagement/stepper/step5-completed.svg",
      description: ` ${this.translate.instant("STEPPER_FINALIZE_MATERIAL")}`,
    },
  ];

  public confirmationOfBannedWasteData: BannedWasteItemModel[] = [];

  public mmControlSheetToPdfData!: MM_ControlSheetToPdfDataModel;

  // *** This function will only be called based on condition during first time page load (onInit) ***
  setCompletedStepFirstTime(stepType: StepType): void {
    const stepIndex = this.stepper.findIndex((step) => step.type === stepType);

    if (stepIndex === -1) return; // Safety check: invalid step

    this.stepper.forEach((step, idx) => {
      if (idx < stepIndex) {
        // Previous steps
        step.enabled = true;
        step.completed = true;
      } else if (idx === stepIndex) {
        // Current step: open, not completed
        step.enabled = true;
        step.completed = false;
      } else {
        // Future steps
        step.enabled = false;
        step.completed = false;
      }
    });

    // Stay on the current step
    this.activeStep.next(this.stepper[stepIndex].type);
  }

  isStepCompleted(stepType: StepType): boolean {
    const step = this.stepper.find((s) => s.type === stepType);
    return step ? step.completed : false;
  }

  // *** This function will be called when any step is completed after any action ***
  setCompletedStep(stepType: StepType): void {
    const stepIndex = this.stepper.findIndex((step) => step.type === stepType);
    this.stepper[stepIndex].completed = true;
    // Enable the next step if it exists
    if (stepType === "identification") {
      if (this.stepper[2]) {
        this.stepper[2].enabled = true;
      }
      if (this.stepper[3]) {
        this.stepper[3].enabled = true;
      }
    }
    if (stepIndex + 1 < this.stepper.length) {
      if (this.stepper[stepIndex + 1].enabled === false) {
        this.stepper[stepIndex + 1].enabled = true;
      }
    }
  }

  resetStepper(): void {
    this.stepper.forEach((step, idx) => {
      if (idx === 0) {
        step.enabled = true;
        step.completed = false;
      } else {
        step.enabled = false;
        step.completed = false;
      }
    });
    this.activeStep.next("globalSearch");
    this.maxStepReached.next(0); // Reset progress
    this.isStepperExpanded.next(true);

    this.stepper = this.stepper.map((step) => ({
      ...step,
      enabled: step.type === "globalSearch",
      completed: false,
    }));
  }

  private getRiskLevelNumber(val: string | number | null | undefined): number {
    if (val === null || val === undefined || val === "") return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const match = val.match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    }
    return 0;
  }

  private normalizeHazards(
    identification: MM_Identification | null | undefined,
  ): string[] {
    if (!identification) return [];
    let hazards = [...(identification.hazards || [])];

    // Health Risk Normalization
    const healthRisk = this.getRiskLevelNumber(
      identification.riskLevel?.healthRiskLevel,
    );
    const seriousChip = "Serious health hazard";
    const healthChip = "Health hazard";
    const toxicityChip = "Acute toxicity";

    if (healthRisk === 1 && !hazards.includes(seriousChip)) {
      hazards.push(seriousChip);
    } else if (healthRisk === 2 && !hazards.includes(healthChip)) {
      hazards.push(healthChip);
    }

    // Check for toxicity mention in H-phrases
    const healthHPhrases: string[] =
      identification.properties?.healthHPhrase || [];
    const mentionsToxicity = healthHPhrases.some((phrase) =>
      phrase?.toLowerCase().includes("toxicity"),
    );
    if (mentionsToxicity && !hazards.includes(toxicityChip)) {
      hazards.push(toxicityChip);
    }

    // Environment Risk Normalization
    const envRisk = this.getRiskLevelNumber(
      identification.riskLevel?.environmentRiskLevel,
    );
    const envChip = "Hazardous to environment";
    if (envRisk === 1 && !hazards.includes(envChip)) {
      hazards.push(envChip);
    }

    return hazards;
  }

  onRowSelectedGlobal(data: MM_InventoryModel) {
    this.mmInventoryForm.patchValue({
      identificationFormGroup: {
        sdsFileInfo: data?.identification?.sdsFileInfo,
        labSheets: [],
        statusSection: {
          status: data.identification?.statusSection?.status,
        },
        materialDetails: {
          materialName: data.identification?.materialDetails?.materialName,
          supplierName: data.identification?.materialDetails?.supplierName,
          state: data.identification?.materialDetails?.state,
          subType: data.identification?.materialDetails?.subType,
          type: data.identification?.materialDetails?.type,
          isImpregnated: data.identification?.materialDetails?.isImpregnated,
        },
        consumtionVolume: {
          value: data?.identification?.consumtionVolume?.value,
          unit: data.identification?.consumtionVolume?.unit,
        },
        environmentalPermitsStatus:
          data?.identification?.environmentalPermitsStatus,
        properties: data.identification?.properties,
        criticalWastes: data.identification?.criticalWastes
          ? data.identification?.criticalWastes
          : [],
        dartxClassification: data.identification?.dartxClassification || "",
        hazards: this.normalizeHazards(data.identification),
        confirmationOfBannedWaste:
          data.identification?.confirmationOfBannedWaste?.map((item) => ({
            ...item,
            isShowPopup: false,
          })) || [],
        riskLevel: data.identification?.riskLevel || {
          fireRiskLevel: "",
          healthRiskLevel: "",
          environmentRiskLevel: "",
        },
      },

      linkToAssetFormGroup: {
        completedZone: data?.completedZone ? data?.completedZone : [],
        addCompletedAssetZone: data?.assetLink?.addCompletedAssetZone
          ? data?.assetLink?.addCompletedAssetZone
          : [],
      },
      siteId: data?.siteId ? data?.siteId : "",
      id: data?.id ? data?.id : "",
      isPublished: data?.isPublished ? data?.isPublished : false,
      masterMaterialId: data?.masterMaterialId ? data?.masterMaterialId : "",
      isClone: data?.isClone ? data?.isClone : false,
      cloneDataInfo: data?.cloneDataInfo,
      ocrExtractedData: {
        sdsFileData: data?.ocrExtractedData?.sdsFileData,
        labSheetsData: data?.ocrExtractedData?.labSheetsData || [],
      },
      ocrFilledFields: data?.ocrFilledFields || [],
      controlSheetPdfInfo: data?.controlSheetPdfInfo,
    });
    if (this.isNotificationFlow$.value) {
      this.mmInventoryForm.disable({ emitEvent: false });
      Object.keys(this.mmInventoryForm.controls).forEach((key) => {
        this.mmInventoryForm.get(key)?.disable({ emitEvent: false });
      });
    } else {
      this.mmInventoryForm.enable({ emitEvent: false });
    }

    // if (
    //   data.identification?.confirmationOfBannedWaste &&
    //   Array.isArray(data.identification.confirmationOfBannedWaste) &&
    //   data.identification.confirmationOfBannedWaste.length > 0
    // ) {
    //   this.confirmationOfBannedWasteData =
    //     data.identification.confirmationOfBannedWaste.map((item) => ({
    //       title: item.title,
    //       isPresent: item.isPresent,
    //       isShowPopup: false,
    //       popupText: item.popupText,
    //       isDisableSubmit: item.isDisableSubmit,
    //     }));
    // } else {
    //   // this.confirmationOfBannedWasteData = [
    //   //   {
    //   //     title: "Radioactive waste",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title: "Anatomical & Infectious medical waste",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title: "Asbestos-containing waste",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title: "Explosives & ammunition / weapons",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title:
    //   //       "Electronic fraction of electrical and electronic waste (e-waste)",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title: "Whole batteries (as dedicated stream)",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title:
    //   //       "Self-reactive / thermally unstable compounds / Organic Peroxides exhibiting explosive properties",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   //   {
    //   //     title:
    //   //       "Unknown or unpredictable composition (e.g. municipal solid waste)",
    //   //     isPresent: null,
    //   //     isShowPopup: false,
    //   //     popupText: "",
    //   //     isDisableSubmit: false,
    //   //   },
    //   // ];
    // }
    if (
      data.identification?.confirmationOfBannedWaste &&
      Array.isArray(data.identification.confirmationOfBannedWaste) &&
      data.identification.confirmationOfBannedWaste.length > 0
    ) {
      this.confirmationOfBannedWasteData =
        data.identification.confirmationOfBannedWaste.map((item) => ({
          title: item.title,
          isPresent: item.isPresent,
          isShowPopup: false,
          popupText: item.popupText,
          isDisableSubmit: item.isDisableSubmit,
        }));
    } else {
      // ✅ Use data from master collection
      const currentData = this.bannedWasteDataSubject.getValue();
      if (currentData && currentData.length > 0) {
        this.confirmationOfBannedWasteData = currentData;
      } else {
        // ✅ Fallback: Wait for data or set default
        this.bannedWasteData$.pipe(take(1)).subscribe((data) => {
          if (data && data.length > 0) {
            this.confirmationOfBannedWasteData = data;
          } else {
            // ✅ Set default if master collection is empty
            this.confirmationOfBannedWasteData = [
              {
                title: "Radioactive waste",
                isPresent: null,
                isShowPopup: false,
                popupText: "",
                isDisableSubmit: false,
              },
              {
                title: "Anatomical & Infectious medical waste",
                isPresent: null,
                isShowPopup: false,
                popupText: "",
                isDisableSubmit: false,
              },
              {
                title: "Asbestos-containing waste",
                isPresent: null,
                isShowPopup: false,
                popupText: "",
                isDisableSubmit: false,
              },
              // ... add all other items
            ];
          }
        });
      }
    }

    // Handle FormArrays correctly
    const labSheetsArray = this.mmInventoryForm.get(
      "identificationFormGroup.labSheets",
    ) as FormArray;
    if (labSheetsArray) {
      labSheetsArray.clear({ emitEvent: false });
      if (
        data.identification?.labSheets &&
        Array.isArray(data.identification.labSheets)
      ) {
        data.identification.labSheets.forEach((sheet) => {
          labSheetsArray.push(
            this.fb.group({
              id: [sheet?.id ?? ""],
              name: [sheet?.name ?? ""],
              size: [sheet?.size ?? ""],
              src: [sheet?.src ?? ""],
              isNewFile: [sheet?.isNewFile ?? false],
            }),
            { emitEvent: false },
          );
        });
      }
    }

    // 2. Handle FormArray manually
    if (data?.id && data.assetLink?.assets?.length > 0) {
      const assetsArray = this.mmInventoryForm.get(
        "linkToAssetFormGroup.assets",
      ) as FormArray;
      assetsArray.clear(); // Clear existing entries
      data.assetLink.assets.forEach((asset: any) => {
        // create same shape as MiLinkToAssetsComponent.createAssetForm
        assetsArray.push(
          this.fb.group({
            uid: [asset?.uid ?? ""],
            id: [asset?.id ?? ""],
            assetZone: this.fb.group({
              area: [asset?.assetZone?.area ?? ""],
              coordinates: this.fb.group({
                lat: [asset?.assetZone?.coordinates?.lat ?? null],
                lng: [asset?.assetZone?.coordinates?.lng ?? null],
              }),
            }),
            isLinked: [!!(asset?.isLinked ?? true)],
          }),
        );
      });
    }
    if (data?.controlSheetData) {
      this.mmControlSheetToPdfData = data?.controlSheetData;
    }
    if (data?.assetLink?.simulatedAssetes?.length > 0) {
      const assetsArray = this.mmInventoryForm.get(
        "linkToAssetFormGroup.simulatedAssetes",
      ) as FormArray;

      assetsArray.clear(); // Clear existing entries

      data?.assetLink?.simulatedAssetes.forEach((asset) => {
        // create same shape as MiLinkToAssetsComponent.createAssetForm
        assetsArray.push(
          this.fb.group({
            uid: [asset?.uid ?? ""],
            zone: [asset?.zone ?? ""],
          }),
        );
      });
    }
    if (data?.isClone) {
      this.mmInventoryForm.markAsDirty();
    }
  }

  setNotificationFlow(value: boolean): void {
    this.isNotificationFlow$.next(value);
  }

  public getMMInventoryMasterData() {
    this.firebaseService.GetMMInventoryMasterData().subscribe({
      next: (response) => {
        this.mmInventoryMasterData.next(response);
      },
      error: (err) => {
        this.mmInventoryMasterData.next(null);
      },
    });
  }

  public getMMInventoryStateUnitData() {
    this.firebaseService.GetMMInventoryStateUnitsData().subscribe({
      next: (response) => {
        this.mmInventoryStateUnitData.next(response);
      },
      error: (err) => {
        this.mmInventoryStateUnitData.next(null);
      },
    });
  }

  public getMaterialHazards() {
    this.httpService.GetMMMaterialHazards().subscribe({
      next: (res) => {
        if (res.status) {
          this.mmHazardsSubject.next(res.data);
        }
      },
      error: (err) => {
        this.mmHazardsSubject.next(null);
      },
    });
  }

  public createMaterial(): Observable<MM_InventoryListResponseModel> {
    const getValueOrReset = (
      key: keyof MM_InventoryModel,
      controlName: string,
    ) => {
      const original = this.mmInventoryForm.get(controlName)?.getRawValue();
      return original;
    };

    const assetsControl = this.mmInventoryForm.get(
      "linkToAssetFormGroup.assets",
    );
    const assets = assetsControl ? assetsControl.value : [];
    if (this.mmControlSheetToPdfData) {
      this.mmControlSheetToPdfData.introduction =
        this.mmControlSheetIntroduction$.value;
      this.mmControlSheetToPdfData.introductionPrompt =
        this.mmControlSheetIntroductionPrompt$.value;
    }

    let payload: MM_InventoryModel = {
      siteId: this.siteId,
      countryId: this.mmInventoryForm.get("countryId")?.value,
      regionId: this.mmInventoryForm.get("regionId")?.value,
      masterMaterialId: this.mmInventoryForm.get("masterMaterialId")?.value,
      identification: this.mmInventoryForm
        .get("identificationFormGroup")
        ?.getRawValue(),
      hazard: this.mmInventoryForm.get("hazardFormGroup")?.getRawValue(),
      assetLink: {
        assets,
        simulatedAssetes: (
          this.mmInventoryForm.get("linkToAssetFormGroup.simulatedAssetes")
            ?.value || []
        ).filter((asset: { uid: string }) => asset?.uid?.trim()),
        addCompletedAssetZone:
          this.mmInventoryForm.get("linkToAssetFormGroup.addCompletedAssetZone")
            ?.value || [],
      } as MM_InventoryModel["assetLink"],
      completedZone:
        this.mmInventoryForm.get("linkToAssetFormGroup.completedZone")?.value ||
        [],

      finalReport: getValueOrReset("finalReport", "confirmationFormGroup"),
      isCertified: this.mmInventoryForm.get("isCertified")?.value,
      stepperInfo: this.mmInventoryForm.get("stepperInfo")?.value,
      status:
        this.mmInventoryForm.get("status")?.value ||
        MM_InventoryStatusEnum.DRAFT,
      isClone: this.mmInventoryForm.get("isClone")?.value,
      isExpertFlow: this.mmInventoryForm.get("isExpertFlow")?.value,
      cloneDataInfo: this.mmInventoryForm.get("cloneDataInfo")?.value,
      controlSheetData: this.mmControlSheetToPdfData
        ? this.mmControlSheetToPdfData
        : undefined,
      ocrExtractedData: this.mmInventoryForm.get("ocrExtractedData")?.value,
      ocrFilledFields: this.mmInventoryForm.get("ocrFilledFields")?.value,
      shouldGenerateControlSheet: this.mmInventoryForm.get(
        "shouldGenerateControlSheet",
      )?.value,
      isConfirmEditing: this.mmInventoryForm.get("isConfirmEditing")?.value,
      controlSheetPdfInfo: this.mmInventoryForm.get("controlSheetPdfInfo")
        ?.value,
    };
    // if (this.mmInventoryForm.get("status")?.value === (MM_InventoryStatusEnum.PENDING_APPROVAL || MM_InventoryStatusEnum.PUBLISHED)) {
    //   payload = {
    //     ...payload,
    //     controlSheetData: this.mmControlSheetToPdfData ? this.mmControlSheetToPdfData : undefined,
    //   }
    // }
    this.currentMMInventoryData = this.mmInventoryForm.get("id")?.value
      ? { id: this.mmInventoryForm.get("id")?.value, ...payload }
      : payload;
    return this.httpService
      .AddMMInventoryItem(
        this.mmInventoryForm.get("id")?.value
          ? { id: this.mmInventoryForm.get("id")?.value, ...payload }
          : payload,
      )
      .pipe(
        catchError((error) => {
          const msg = error?.error?.message || "Material creation failed";
          return throwError(() => new Error(msg));
        }),
      );
  }

  onResetForm() {
    this.mmInventoryForm?.reset();
  }
  MM_SafetySheetToExtractData(base64pdf: string) {
    this.httpService.MM_SafetySheetToExtractData(base64pdf).subscribe({
      next: (res) => {
        // if (res.status) {
        //   // Optionally notify component or update list
        // }
      },
      error: (err) => {},
    });
  }

  setControlSheetData(data: MM_ControlSheetToPdfDataModel) {
    data.id = this.mmInventoryForm.get("id")?.value;
    this.mmControlSheetToPdfData = data;
  }

  GenerateIntroductionForMMControlSheet(materialName: string): Observable<any> {
    return this.httpService.GenerateIntroductionForMMControlSheet(materialName);
  }

  get autosaveCompleted$(): Observable<void> {
    return this.autosaveCompleteSubject.asObservable();
  }

  notifyAutosaveComplete() {
    this.autosaveCompleteSubject.next();
  }

  async OCR_ExtractedData(docId: string, fileType: "sds" | "labSheet" = "sds") {
    const loading = await this.loadingCtrl.create({
      spinner: "bubbles",
      keyboardClose: true,
    });
    await loading.present();

    const mmInventoryFormSub = this.firebaseService
      .GetMMInventoryDataByDocId(docId)
      .subscribe({
        next: (res) => {
          if (res?.ocrExtractedData) {
            if (
              fileType == "labSheet" &&
              res?.ocrExtractedData?.labSheetsData &&
              res?.ocrExtractedData?.labSheetsData.length > 0
            ) {
              let latestLabSheetData =
                res?.ocrExtractedData?.labSheetsData[
                  res?.ocrExtractedData?.labSheetsData?.length - 1
                ];
              this.Prepare_OCR_ExtractedData(latestLabSheetData, "lab_sheet");
            } else if (fileType == "sds") {
              this.Prepare_OCR_ExtractedData(
                res?.ocrExtractedData?.sdsFileData,
                "sds",
              );
            }
            this.dropDownControl.next(true);
            mmInventoryFormSub?.unsubscribe();
            loading.dismiss();
            this.mmInventoryForm.markAsDirty();
          }
        },
      });
  }

  private prepareValidOCRPatch(
    ocrData: OCRExtractedDataModel,
  ): OCRValidatedPatch {
    const formattedState = this.formatMaterialState(ocrData?.state);
    const flashPointCelsius = sanitizeFlashPoint(
      ocrData?.properties?.flashPoint,
    );
    const matchTypeAndSubtype = this.getMatchedMaterialTypeAndSubtype(
      ocrData?.type || "",
      ocrData?.subType || "",
    );
    const isValidState = Boolean(
      formattedState &&
      this.mmInventoryStateUnitData
        .getValue()
        ?.some((sInd) => sInd.state === formattedState),
    );
    const isValidType = Boolean(
      matchTypeAndSubtype?.type &&
      this.mmInventoryMasterData
        .getValue()
        ?.some(
          (inventoryItem) => inventoryItem.type === matchTypeAndSubtype.type,
        ),
    );
    const materialDetails: OCRPatchMaterialDetails = {};
    const currentMaterialName = this.mmInventoryForm.get(
      "identificationFormGroup.materialDetails.materialName",
    )?.value;
    if (!currentMaterialName || currentMaterialName.trim() === "") {
      const materialName = ocrData?.materialName?.trim();
      if (materialName) {
        materialDetails.materialName = materialName;
      }
    }

    if (
      isValidState &&
      typeof formattedState === "string" &&
      formattedState?.length > 0
    ) {
      materialDetails.state = formattedState;
    }

    if (isValidType) {
      const typeValue = matchTypeAndSubtype.type;
      if (typeValue !== null && typeof typeValue === "string") {
        materialDetails.type = typeValue;
      }

      const subTypeValue = matchTypeAndSubtype.subType;
      if (subTypeValue !== null && typeof subTypeValue === "string") {
        materialDetails.subType = subTypeValue;
      }
    }

    const properties: OCRPatchProperties = {};
    const props = ocrData?.properties;

    // Helper function to safely convert to string
    const toSafeString = (value: unknown): string | undefined => {
      if (value === null || value === undefined) return undefined;
      const str = String(value).trim();
      return str.length > 0 ? str : undefined;
    };

    const rawParticleDistribution = props?.particleSizeDistribution;
    const particleSizeDistribution = sanitizeParticleSizeDistribution(
      rawParticleDistribution,
    );

    if (particleSizeDistribution) {
      properties.particleSizeDistribution = particleSizeDistribution;
    }

    // Always assign (even "") — sanitizeFlashPoint always returns a string,
    // and an explicit "" lets the UI correctly treat this as "no usable value"
    properties.flashPoint = flashPointCelsius;

    const calorificValue = this.extractAndConvertCalorificValue(
      props?.calorificValue,
    );
    if (calorificValue) {
      properties.calorificValue = calorificValue;
    }

    const moisture = toSafeString(props?.moisture);
    if (moisture) {
      properties.moisture = moisture;
    }

    const pH = this.extractPHValue(props?.pH);
    if (pH) {
      properties.pH = pH;
    }

    const patch: OCRValidatedPatch = {
      identificationFormGroup: {},
    };

    if (Object.keys(materialDetails)?.length > 0) {
      patch.identificationFormGroup.materialDetails = materialDetails;
    }
    if (Object.keys(properties)?.length > 0) {
      patch.identificationFormGroup.properties = properties;
    }

    return patch;
  }

  async Prepare_OCR_ExtractedData(
    ocrExtractedData: OCRExtractedDataModel,
    category: "sds" | "lab_sheet",
    alreadyMatched: boolean = false,
  ): Promise<void> {
    const mappingData = this.getPropertyMappingData();
    const validPatch = this.prepareValidOCRPatch(ocrExtractedData);
    const mappedProps: OCRPatchProperties = {};
    const props = ocrExtractedData?.properties;

    const dusty = await this.OCR_mapPropertyValue(
      "dusty",
      "Dusty",
      props?.dusty || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (dusty) mappedProps.dusty = dusty;

    const foreignBodies = await this.OCR_mapPropertyValue(
      "foreignBodies",
      "Foreign bodies",
      props?.foreignBodies || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (foreignBodies) mappedProps.foreignBodies = foreignBodies;

    const odor = await this.OCR_mapPropertyValue(
      "odor",
      "Odor",
      props?.odor || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (odor) mappedProps.odor = odor;

    const stickiness = await this.OCR_mapPropertyValue(
      "stickiness",
      "Stickiness",
      props?.stickiness || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (stickiness) mappedProps.stickiness = stickiness;

    const particles = await this.OCR_mapPropertyValue(
      "particles",
      "Particles",
      props?.particles || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (particles) mappedProps.particles = particles;

    const sedimentation = await this.OCR_mapPropertyValue(
      "sedimentation",
      "Sedimentation",
      props?.sedimentation || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (sedimentation) mappedProps.sedimentation = sedimentation;

    const pumpable = await this.OCR_mapPropertyValue(
      "pumpable",
      "Pumpable",
      props?.pumpable || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (pumpable) mappedProps.pumpable = pumpable;

    const viscosity = await this.OCR_mapPropertyValue(
      "viscosity",
      "Viscosity",
      props?.viscosity || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (viscosity) mappedProps.viscosity = viscosity;

    const flowability = await this.OCR_mapPropertyValue(
      "flowability",
      "Flowability",
      props?.flowability || "",
      ocrExtractedData?.fileInfo,
      category,
    );
    if (flowability) mappedProps.flowability = flowability;
    if (Object.keys(mappedProps).length > 0) {
      if (!validPatch.identificationFormGroup.properties) {
        validPatch.identificationFormGroup.properties = {};
      }
      validPatch.identificationFormGroup.properties = {
        ...validPatch.identificationFormGroup.properties,
        ...mappedProps,
      };
    }

    const rawHealth = ocrExtractedData?.properties?.healthHPhrase;
    const rawEnvironment = ocrExtractedData?.properties?.environmentHPhrase;
    const rawFire = ocrExtractedData?.properties?.fireHPhrase;

    const ocrHealthList: string[] = Array.isArray(rawHealth)
      ? rawHealth
      : rawHealth
        ? [rawHealth]
        : [];

    const ocrEnvironmentList: string[] = Array.isArray(rawEnvironment)
      ? rawEnvironment
      : rawEnvironment
        ? [rawEnvironment]
        : [];
    const ocrFireList: string[] = Array.isArray(rawFire)
      ? rawFire
      : rawFire
        ? [rawFire]
        : [];

    if (alreadyMatched) {
      // These values were already matched against the correct, state-specific
      // hazard lists and confirmed by the user in the OCR review modal
      // (see MiIdentificationComponent.syncOcrHazardSelections /
      // onDiScanningConfirm). Re-running findMatchedHazards here against a
      // *different*, non-state-aware hazard source (this.mmHazardList$) is
      // what previously caused the modal's "Selected (N)" count to drop or
      // disappear after Confirm. Trust the caller's values as-is so the form
      // ends up with exactly what the modal showed.
      if (ocrHealthList.length || ocrEnvironmentList.length || ocrFireList.length) {
        if (!validPatch.identificationFormGroup.properties) {
          validPatch.identificationFormGroup.properties = {};
        }
        if (ocrHealthList.length) {
          validPatch.identificationFormGroup.properties.healthHPhrase =
            ocrHealthList;
        }
        if (ocrEnvironmentList.length) {
          validPatch.identificationFormGroup.properties.environmentHPhrase =
            ocrEnvironmentList;
        }
        if (ocrFireList.length) {
          validPatch.identificationFormGroup.properties.fireHPhrase =
            ocrFireList;
        }
      }

      this.mmInventoryForm?.patchValue(validPatch, { emitEvent: true });
      return;
    }

    // --- Raw / unmatched OCR data path (e.g. OCR_ExtractedData()) ---
    // Used when this function receives data straight from OCR/Firestore that
    // has never been through the modal's matching step.
    let healthHazardList: string[] = [];
    let environmentHazardList: string[] = [];
    let fireHazardList: string[] = [];
    try {
      const hazardData = await firstValueFrom(
        this.mmHazardList$.pipe(
          filter((data) => !!data && data.length > 0),
          take(1),
          timeout(5000), // 5-second timeout to prevent infinite waiting
        ),
      );

      if (hazardData && hazardData.length > 0) {
        healthHazardList = hazardData[0]?.health.map(
          (list) => `${list.level}-${list.description}`,
        );
        environmentHazardList = hazardData[0]?.environment.map(
          (list) => `${list.level}-${list.description}`,
        );
        fireHazardList = hazardData[0]?.fire.map(
          (list) => `${list.level}-${list.description}`,
        );
      }
    } catch (error) {
      console.error(
        "Failed to load hazard master list for OCR matching; falling back to raw OCR phrases.",
        error,
      );
    }

    // findMatchedHazards always preserves the raw phrase when no match is
    // found (including when the master list itself failed to load), so
    // OCR-extracted phrases are never silently discarded here.
    const matchedHealthHazards = this.findMatchedHazards(
      ocrHealthList,
      healthHazardList,
    );
    const matchedEnvironmentHazards = this.findMatchedHazards(
      ocrEnvironmentList,
      environmentHazardList,
    );
    const matchedFireHazards = this.findMatchedHazards(
      ocrFireList,
      fireHazardList,
    );

    if (matchedHealthHazards?.length > 0 || matchedEnvironmentHazards?.length > 0 || matchedFireHazards?.length > 0) {
      if (!validPatch.identificationFormGroup.properties) {
        validPatch.identificationFormGroup.properties = {};
      }
    }

    if (matchedHealthHazards?.length > 0) {
      validPatch.identificationFormGroup.properties!.healthHPhrase =
        matchedHealthHazards;
    }

    if (matchedEnvironmentHazards?.length > 0) {
      validPatch.identificationFormGroup.properties!.environmentHPhrase =
        matchedEnvironmentHazards;
    }
    if (matchedFireHazards?.length > 0) {
      validPatch.identificationFormGroup.properties!.fireHPhrase =
        matchedFireHazards;
    }

    this.mmInventoryForm?.patchValue(validPatch, { emitEvent: true });
  }

  async OCR_mapPropertyValue(
    propertyId: string,
    propertyName: string,
    value: string,
    fileInfo?: FileModel,
    fileCategory?: "sds" | "lab_sheet",
  ): Promise<"High" | "Medium" | "Low" | null> {
    const propMap = this.propertyMappingData?.find((m) => m.id === propertyId);
    if (!value) return null;

    const mm_inventory_id = this.mmInventoryForm.get("id")?.value;
    const materialDetails = this.mmInventoryForm.get(
      "identificationFormGroup.materialDetails",
    )?.value;
    const materialName = materialDetails?.materialName || "";
    const siteId = this.mmInventoryForm.get("siteId")?.value;
    this.store.select("site").subscribe((site: SiteModel) => {
      this.siteName = site?.locations?.site?.location || "";
    });
    const siteName = this.siteName || "";
    const propertyMapBody: AddOCRNotMappedPropertyReqBodyModel = {
      value,
      siteId,
      siteName,
      propertyId,
      materialName,
      propertyName,
      mm_inventory_id,
      fileInfo: {
        id: fileInfo?.id || "",
        name: fileInfo?.name || "",
        src: fileInfo?.src || "",
      },

      fileCategory,
    };
    if (!propMap) {
      this.httpService
        .AddOCRNotMappedPropertyData(propertyMapBody)
        .pipe(take(1))
        .subscribe();
      return null;
    }

    if (propMap?.high?.includes(value)) return "High";
    if (propMap?.medium?.includes(value)) return "Medium";
    if (propMap?.low?.includes(value)) return "Low";

    this.httpService
      .AddOCRNotMappedPropertyData(propertyMapBody)
      .pipe(take(1))
      .subscribe();
    return null;
  }

  loadPropertyMappingData(): Observable<MM_OCRPropertiesLevelMapping[]> {
    if (this.propertyMappingData) {
      return of(this.propertyMappingData);
    }

    return this.firebaseService.GetMMInventoryOCRPropertyMappingData().pipe(
      tap((data) => {
        this.propertyMappingData = data;
        this.propertyMappingLoaded$.next(true);
      }),
    );
  }

  getPropertyMappingData(): MM_OCRPropertiesLevelMapping[] | null {
    return this.propertyMappingData;
  }
  propertyMappingReady(): Observable<boolean> {
    return this.propertyMappingLoaded$.asObservable().pipe(
      filter((ready) => ready === true),
      take(1),
    );
  }

  getMatchedMaterialTypeAndSubtype(
    rawMaterialType: string,
    rawMaterialSubtype: string,
  ): { type: string | null; subType: string | null } {
    if (!this.materialTypes?.length) {
      return { type: null, subType: null };
    }

    const normalize = (str: string) =>
      str
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, " ") // collapse multiple spaces
        .trim();

    // 🔹 Bidirectional match for plural / minor variations
    const matches = (leftValue: string, rightValue: string) => {
      if (!leftValue || !rightValue) return false;

      const normalizedLeft = normalize(leftValue);
      const normalizedRight = normalize(rightValue);

      // Regex: optional trailing 's' for either string
      const patternLeft = new RegExp(`^${normalizedLeft}s?$`);
      const patternRight = new RegExp(`^${normalizedRight}s?$`);

      return (
        patternLeft.test(normalizedRight) || patternRight.test(normalizedLeft)
      );
    };

    // Step 1️⃣: Match main type
    const matchedType: MMInventoryMasterModel | undefined = rawMaterialType
      ? this.materialTypes.find((item: MMInventoryMasterModel) =>
          matches(item?.type, rawMaterialType),
        )
      : undefined;

    const result: { type: string | null; subType: string | null } = {
      type: matchedType?.type ?? null,
      subType: null,
    };

    // Step 2️⃣: Match subtype (if type found)
    if (
      matchedType &&
      rawMaterialSubtype &&
      Array.isArray(matchedType.subType)
    ) {
      const sub = matchedType.subType.find((sub: string) =>
        matches(sub, rawMaterialSubtype),
      );
      result.subType = sub ?? null;
    }

    return result;
  }

  extractHCode(text: string | null | undefined): string | null {
    if (!text) return null;
    const match = text.match(/\bH?\s*-?\s*(\d{3,4})\b/i);
    if (!match) return null;
    return `H${match[1]}`.toUpperCase();
  }

  normalizeHazardStatement(text: string | null | undefined): string {
    if (!text) return "";
    return text
      .replace(/^H?\s*-?\s*\d{3,4}\s*[-–—:]*\s*/i, "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  findHazardByHCode(
    hCode: string | null,
    knownHazards: string[] = [],
  ): string | null {
    if (!hCode || !knownHazards.length) return null;
    const normalizedCode = hCode.toUpperCase();
    return (
      knownHazards.find(
        (hazard) => this.extractHCode(hazard)?.toUpperCase() === normalizedCode,
      ) || null
    );
  }

  findHazardByStatement(
    ocrStatement: string,
    knownHazards: string[] = [],
  ): string | null {
    if (!ocrStatement || !knownHazards.length) return null;
    const normalizedOcr = this.normalizeHazardStatement(ocrStatement);
    if (!normalizedOcr) return null;

    const exactMatch = knownHazards.find(
      (hazard) => this.normalizeHazardStatement(hazard) === normalizedOcr,
    );
    if (exactMatch) return exactMatch;

    let bestMatch: string | null = null;
    let highestScore = 0;
    const CONFIDENCE_THRESHOLD = 0.8;

    knownHazards.forEach((hazard) => {
      const normalizedHazard = this.normalizeHazardStatement(hazard);
      const score = this.getSimilarityScore(normalizedOcr, normalizedHazard);
      if (score > highestScore) {
        highestScore = score;
        bestMatch = hazard;
      }
    });

    return highestScore >= CONFIDENCE_THRESHOLD ? bestMatch : null;
  }

  private getSimilarityScore(firstText: string, secondText: string): number {
    if (!firstText || !secondText) return 0;
    if (firstText === secondText) return 1;

    const shorter =
      firstText?.length <= secondText?.length ? firstText : secondText;
    const longer =
      firstText?.length > secondText?.length ? firstText : secondText;
    if (longer?.startsWith(shorter)) {
      const ratio = shorter?.length / longer?.length;
      if (ratio >= 0.75) return 0.9;
    }

    const distance = this.getLevenshteinDistance(firstText, secondText);
    const maxLen = Math.max(firstText?.length, secondText?.length);
    return maxLen > 0 ? 1 - distance / maxLen : 0;
  }

  private getLevenshteinDistance(
    firstText: string,
    secondText: string,
  ): number {
    const totalRows = (firstText?.length ?? 0) + 1;
    const totalCols = (secondText?.length ?? 0) + 1;
    const distanceMatrix = Array.from({ length: totalRows }, () =>
      new Array(totalCols).fill(0),
    );

    for (let rowIndex = 0; rowIndex < totalRows; rowIndex += 1) {
      distanceMatrix[rowIndex][0] = rowIndex;
    }

    for (let colIndex = 0; colIndex < totalCols; colIndex += 1) {
      distanceMatrix[0][colIndex] = colIndex;
    }

    for (let rowIndex = 1; rowIndex < totalRows; rowIndex += 1) {
      for (let colIndex = 1; colIndex < totalCols; colIndex += 1) {
        const substitutionCost =
          firstText[rowIndex - 1] === secondText[colIndex - 1] ? 0 : 1;

        distanceMatrix[rowIndex][colIndex] = Math.min(
          distanceMatrix[rowIndex - 1][colIndex] + 1, // Deletion
          distanceMatrix[rowIndex][colIndex - 1] + 1, // Insertion
          distanceMatrix[rowIndex - 1][colIndex - 1] + substitutionCost, // Substitution
        );
      }
    }
    return distanceMatrix[totalRows - 1][totalCols - 1];
  }

  findMatchedHazards(
    ocrHealthList: string[] = [],
    knownHealthList: string[] = [],
  ): string[] {
    if (!ocrHealthList.length) return [];

    const seenKeys = new Set<string>();
    const matches: string[] = [];

    for (const item of ocrHealthList) {
      const cleanItem = item.replace(/^[*•\s-]+/, "").trim();
      if (!cleanItem) continue;

      const hCode = this.extractHCode(cleanItem);
      let resolved: string | null = null;

      if (hCode && knownHealthList.length) {
        resolved = this.findHazardByHCode(hCode, knownHealthList);
      }
      if (!resolved && knownHealthList.length) {
        resolved = this.findHazardByStatement(cleanItem, knownHealthList);
      }
      if (!resolved) {
        resolved = cleanItem; // always preserve raw text as last resort
      }

      // SDS documents commonly list the same hazard phrase twice, worded
      // slightly differently (e.g. "May cause drowsiness or dizziness" vs.
      // "May cause drowsiness and dizziness."), with no H-code in either
      // variant. Deduping on the raw string (the old behavior) let both
      // survive as two separate entries. Dedupe on the H-code when one is
      // available (from the OCR text, or recovered from the matched
      // canonical phrase); only fall back to normalized text comparison
      // when no H-code exists anywhere.
      const dedupKey =
        hCode ||
        this.extractHCode(resolved) ||
        resolved.toLowerCase().replace(/\s+/g, " ").trim();

      if (seenKeys.has(dedupKey)) continue;
      seenKeys.add(dedupKey);
      matches.push(resolved);
    }

    return matches;
  }

  /**
   * Like findMatchedHazards, but does NOT fall back to the raw OCR phrase
   * when no H-code or statement match is found against the known list.
   * Use this wherever the caller needs to know if OCR gave us a *usable*
   * (known-list) value — e.g. deciding captured vs. "couldn't be found" in
   * the review modal — as opposed to findMatchedHazards, which always
   * preserves raw text so a phrase is never silently dropped when actually
   * applied to the form.
   */
  findStrictlyMatchedHazards(
    ocrList: string[] = [],
    knownList: string[] = [],
  ): string[] {
    if (!ocrList.length || !knownList.length) return [];

    const seenKeys = new Set<string>();
    const matches: string[] = [];

    for (const item of ocrList) {
      const cleanItem = item.replace(/^[*•\s-]+/, "").trim();
      if (!cleanItem) continue;

      const hCode = this.extractHCode(cleanItem);
      let resolved: string | null = null;

      if (hCode) {
        resolved = this.findHazardByHCode(hCode, knownList);
      }
      if (!resolved) {
        resolved = this.findHazardByStatement(cleanItem, knownList);
      }
      if (!resolved) continue; // strict: drop unmatched, no raw fallback

      const dedupKey =
        hCode ||
        this.extractHCode(resolved) ||
        resolved.toLowerCase().replace(/\s+/g, " ").trim();

      if (seenKeys.has(dedupKey)) continue;
      seenKeys.add(dedupKey);
      matches.push(resolved);
    }

    return matches;
  }

  extractAndConvertCalorificValue(
    rawCalorific: string | number | undefined | null,
  ): string | null {
    if (rawCalorific === null || rawCalorific === undefined) return null;

    const valueStr = String(rawCalorific)?.trim();
    if (!valueStr) return null;

    // 1. Extract number and unit
    const match = valueStr?.match(
      /([\d,.]+)\s*(MJ\/kg|kJ\/kg|kcal\/kg|cal\/g|BTU\/lb)/i,
    );

    if (!match) {
      const numericMatch = valueStr?.match(/[\d,.]+/);
      return numericMatch ? `${numericMatch[0]?.replace(",", "")}` : null;
    }

    // 2. Parse the numerical value (remove commas if any)
    let value = parseFloat(match[1]?.replace(",", ""));
    const unit = match[2]?.toLowerCase();

    // 3. Conversion Switch
    switch (unit) {
      case "kj/kg":
        value = value / 1000;
        break;
      case "kcal/kg":
      case "cal/g":
        value = value * 0.0041868;
        break;
      case "btu/lb":
        value = value * 0.002326;
        break;
      case "mj/kg":
      default:
        // No change needed
        break;
    }

    // 4. Return formatted result (rounded to 4 decimal places for precision)
    return `${parseFloat(value.toFixed(4))}`;
  }

  extractPHValue(rawPH: string | number | undefined | null): string | null {
    if (rawPH === null || rawPH === undefined) return null;

    let value = String(rawPH).trim();
    if (!value) return null;
    const isInvalid =
      /not\s*found|not\s*available|no\s*data|not\s*applicable|nincs\s*adat|^na$/i.test(
        value,
      );
    if (isInvalid) return null;
    let normalized = value
      .replace(/,/g, ".")
      .replace(/\s+to\s+/gi, "-")
      .replace(/~/g, "-");
    const regex = /([<>]=?)?\s*([\d.]+)(?:\s*[-–—]\s*([\d.]+))?/;

    const match = normalized.match(regex);
    if (!match) return null;

    const prefix = match[1] || "";
    const start = match[2];
    const end = match[3];
    if (end) {
      return `${prefix}${start}-${end}`;
    }
    return `${prefix}${start}`;
  }

  public formatMaterialState(rawState: string | undefined): string {
    if (!rawState) return "";

    const cleanState = rawState
      .toLowerCase()
      .replace(/[^\p{L}\s]/gu, "")
      .trim();

    const stateMap: Record<string, string[]> = {
      Solid: [
        "fest",
        "solid",
        "твърд",
        "vastu",
        "čvrst",
        "stary",
        "solide",
        "sólido",
        "stewig",
        "στερεό",
        "twardy",
        "szilárd",
        "solidne",
        "твёрдый",
        "stisknutý",
      ],
      Liquid: [
        "υγρό",
        "течен",
        "liquid",
        "płynny",
        "жидкий",
        "liquide",
        "líquido",
        "flüssig",
        "tekućina",
        "tekutina",
        "folyékony",
        "vloeistof",
      ],
      Gas: ["gas", "gaz", "gás", "газ", "gáz", "αέριο", "plin"],
    };

    for (const [key, synonyms] of Object.entries(stateMap)) {
      if (synonyms.some((sIndex) => cleanState.includes(sIndex))) {
        return key;
      }
    }

    return rawState.charAt(0).toUpperCase() + rawState.slice(1).toLowerCase();
  }

  public getMaterialHealthHazards(state?: string) {
    const targetState =
      state ||
      this.mmInventoryForm.get("identificationFormGroup.materialDetails.state")
        ?.value;

    if (targetState) {
      this.firebaseService.GetMMHazardHealthData(targetState).subscribe({
        next: (res) => {
          if (res) {
            this.mmHazardHealthDataSubject.next(res);
          }
        },
        error: (err) => {
          this.mmHazardHealthDataSubject.next(null);
        },
      });
    } else {
      this.mmHazardHealthDataSubject.next(null);
    }
  }
  getMaterialEnvironmentHazards(state?: string): void {
    this.firebaseService.getMMEnvironmentHazardData(state).subscribe({
      next: (data) => {
        if (data && data.length > 0) {
          this.environmentHazardSubject.next(data);
        } else {
          this.environmentHazardSubject.next([]);
        }
      },
      error: (err) => {
        console.error("Error fetching environment hazards:", err);
        this.environmentHazardSubject.next([]);
      },
    });
  }

  //#region Edit Lock Feature
  // ================================
  // 🔐 LOCK MATERIAL
  // ================================
  LockMMInventoryForEditing(materialId: string) {
    if (!materialId) return;
    this.firebaseService
      .acquireMMRegistrationLock(materialId, {
        id: this.currentUser?.id || "",
        name:
          this.currentUser?.firstName + " " + this.currentUser?.lastName || "",
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((success) => {
        if (success) {
          this.startLockHeartbeat(materialId);
        }
      });
  }
  // ================================
  // 🔄 HEARTBEAT REFRESH (every 2 min)
  // ================================
  private startLockHeartbeat(materialId: string) {
    this.lockRefreshInterval = setInterval(
      () => {
        this.firebaseService
          .refreshMMRegistrationLock(materialId, {
            id: this.currentUser?.id || "",
            name:
              this.currentUser?.firstName + " " + this.currentUser?.lastName ||
              "",
          })
          .catch(console.error);
      },
      1 * 60 * 1000,
    ); // every 1 minute
  }
  // ================================
  // 🔓 RELEASE LOCK
  // ================================
  releaseEditLock() {
    const materialId = this.mmInventoryForm.get("id")?.value;
    if (!materialId) return;
    if (this.lockRefreshInterval) {
      clearInterval(this.lockRefreshInterval);
      this.lockRefreshInterval = null;
    }
    this.firebaseService
      .releaseMMRegistrationLock(materialId)
      .catch(console.error);
  }

  private readonly LOCK_DURATION = 1 * 60 * 1000; // 1 minute

  checkEditLock(materialId: string): Observable<MMLockModel | null> {
    return this.firebaseService.checkMMRegistrationLock(materialId);
  }
  get hazards$() {
    if (!this.hazardsLoaded) {
      this.hazardsLoaded = true;
      this.firebaseService.getHazardsInfo().subscribe({
        next: (data) => {
          this.hazardsSubject.next({
            fireHazards: data?.fireHazards || [],
            healthHazards: data?.healthHazards || [],
            environmentHazards: data?.environmentHazards || [],
          });
        },
        error: (err) => {
          this.hazardsLoaded = false;
        },
      });
    }
    return this.hazardsSubject.asObservable();
  }
  loadLeachateTestPdf() {
    this.firebaseService
      .getFileFromInputHelp("leachate_test_pdf")
      .pipe(take(1))
      .subscribe({
        next: (file: PdfInputHelpFile | null) => {
          if (file) {
            const fileModel: FileModel = {
              src: file?.fileSrc,
              url: file?.fileSrc,
              name: file?.fileName,
              fileType: "pdf",
            };
            this.leachateTestPdfSubject.next(fileModel);
          } else {
            this.leachateTestPdfSubject.next(null);
          }
        },
        error: () => {
          this.leachateTestPdfSubject.next(null);
        },
      });
  }

  loadBurnTestPdf() {
    this.firebaseService
      .getFileFromInputHelp("burn_test")
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (file: PdfInputHelpFile | null) => {
          if (file) {
            const fileModel: FileModel = {
              src: file?.fileSrc,
              url: file?.fileSrc,
              name: file?.fileName,
              fileType: "pdf",
            };
            this.burnTestPdfSubject.next(fileModel);
          } else {
            this.burnTestPdfSubject.next(null);
          }
        },
        error: () => {
          this.burnTestPdfSubject.next(null);
        },
      });
  }

  getLeachateTestPdf(): FileModel | null {
    return this.leachateTestPdfSubject.getValue();
  }

  getBurnTestPdf(): FileModel | null {
    return this.burnTestPdfSubject.getValue();
  }

  //#endregion
  ngOnDestroy(): void {
    this.releaseEditLock();
    if (this.lockRefreshInterval) {
      clearInterval(this.lockRefreshInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  requestUpdateOnsubmitted(requestStatus?: StatusEnumModel) {
    const materialId = this.mmInventoryForm.get("id")?.value;
    if (!materialId) return;
    // Determine request status based on MM expert role

    const payload: Partial<MM_RiskLevelRequestUpdateInfo_Model> = {
      id: uuidv4(),
      materialId,
      createdAt: new Date().toISOString(),
      requestStatus: requestStatus || StatusEnumModel.PENDING_APPROVAL,
      fireRiskLevel:
        this.riskLevelRequestUpdateForm.get("fireRiskLevel")?.value || "",
      environmentRiskLevel:
        this.riskLevelRequestUpdateForm.get("environmentRiskLevel")?.value ||
        "",
      healthRiskLevel:
        this.riskLevelRequestUpdateForm.get("healthRiskLevel")?.value || "",

      environmentHazards:
        this.riskLevelRequestUpdateForm.get("environmentHazards")?.value || [],
      healthHazards:
        this.riskLevelRequestUpdateForm.get("healthHazards")?.value || [],
      fireHazards:
        this.riskLevelRequestUpdateForm.get("fireHazards")?.value || "",
      remarks: this.riskLevelRequestUpdateForm.get("remarks")?.value || "",

      requestedByUserName:
        this.currentUser?.firstName + " " + this.currentUser?.lastName || "",
      requestedByEmail: this.currentUser?.email || "",
    };
    this.firebaseService
      .addUpdateMMInventoryReqUpdateData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  requestUpdateOnApproval(
    isApproved: boolean,
  ): Observable<UpdateRiskLevelResponseModel> {
    const documentId = this.riskLevelRequestUpdateForm.get("id")?.value;
    if (!documentId) {
      this.toastService.toastFailed(
        this.translate.instant("SERVICES_REQUEST_ID_MISSING_APPROVAL_ACTION"),
      );
      return throwError(() => new Error("Request ID missing"));
    }

    const rejectionReason =
      this.riskLevelRequestUpdateForm
        .get("rejectionReason")
        ?.value?.toString()
        ?.trim() || "";

    if (!isApproved && !rejectionReason) {
      this.toastService.toastFailed(
        this.translate.instant("SERVICES_REJECTION_REASON_REQUIRED"),
      );
      return throwError(() => new Error("Rejection reason missing"));
    }

    const requestStatus = isApproved
      ? StatusEnumModel.APPROVED
      : StatusEnumModel.REJECTED;

    const endpointPayload = {
      id: documentId as string,
      requestStatus,
      rejectionReason: !isApproved ? (rejectionReason as string) : undefined,
    };

    return this.httpService
      .UpdateRiskLevelRequestByExpert(endpointPayload)
      .pipe(
        take(1),
        // catchError((error: any) => {
        //   this.toastService.toastFailed(
        //     `Failed to ${isApproved ? "approve" : "reject"} risk level request.`,
        //   );
        //   return throwError(() => error);
        // })
      );
  }

  private riskLevelReqUpdateSub?: Subscription;

  getRiskLevelRequestedData() {
    let materialId = this.mmInventoryForm.get("id")?.value;
    let materialStatus = this.mmInventoryForm.get("status")?.value;

    this.riskLevelRequestUpdateDataSubject.next(null);
    if (!materialId) return;

    if (this.riskLevelReqUpdateSub) {
      this.riskLevelReqUpdateSub.unsubscribe();
    }

    this.riskLevelReqUpdateSub = this.firebaseService
      ?.getMMReqUpdateDataByDocId(materialId)
      .subscribe({
        next: (res) => {
          const isMaterialPublished =
            String(materialStatus).toLowerCase() === "published";
          if (isMaterialPublished && res?.requestStatus === "Rejected") {
            this.riskLevelRequestUpdateDataSubject.next(null);
          } else {
            this.riskLevelRequestUpdateDataSubject.next(res);
          }
        },
        error: (err) => {
          this.riskLevelRequestUpdateDataSubject.next(null);
        },
      });
  }

  GoToMaterialRegistrationTab() {
    this.router.navigate(["/settings"], {
      state: { tab: "MaterialManagement", materialManagementTab: "MaterialManagement" },
    });
  }

  GoToMaterialManagementTab() {
    this.router.navigate(["/admin"], {
      state: { tab: "control-inventory", materialManagementTab: "Material" },
    });
  }
}
